import { useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { useToast } from '@givebutter/primitives/toast';
import { faCircle1, faCircle2 } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createEngageDomain as createEngageDomainApi, getEngageDomains } from 'api/engage';
import { DNSRecordTable } from 'assets/js/pages/engage/components/DomainSettings/DNSRecordTable';
import { DNSVerification } from 'assets/js/pages/engage/components/DomainSettings/DNSVerification';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { transformEngageDomainQuery } from 'helpers/reactQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

type DomainType = 'customDomain' | 'givebutterDomain' | '';

interface AddDomainFormValues {
    domain: string;
    is_subdomain: boolean;
    reply_to: string;
}

const AddDomainDrawer = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const [selectedDomainType, setSelectedDomainType] = useState<DomainType>('');
    const { engageSubdomains = false } = useFlags();

    const { data: domains } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageDomains(accountId),
        queryFn: () => {
            return getEngageDomains(accountId);
        },
        ...transformEngageDomainQuery(),
    });

    const methods = Form.useForm<AddDomainFormValues>({
        defaultValues: {
            domain: '',
            is_subdomain: false,
            reply_to: '',
        },
    });
    const { handleSubmit, setValue, resetFormButton, setError, path, control } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: ({ domain, is_subdomain, reply_to }: AddDomainFormValues) =>
            createEngageDomainApi(accountId, {
                domain,
                is_subdomain,
                reply_to,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageDomains(accountId),
            });
            toast.success('Successfully added domain');
            if (selectedDomainType === 'givebutterDomain') {
                history.push(`/accounts/${accountId}/engage/settings/domains`);
            }
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    setError(key as FieldPath<AddDomainFormValues>, {
                        message: errors[key][0],
                    });
                });
            } else {
                toast.error('Unknown error adding domain');
            }
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <Drawer.Header>
                <Drawer.Title>Add new domain</Drawer.Title>
            </Drawer.Header>
            <Drawer.Body>
                <div className="flex flex-col gap-8">
                    <RadioGroup.Root
                        onValueChange={(value: DomainType) => {
                            setValue('is_subdomain', value === 'givebutterDomain');
                            setSelectedDomainType(value);
                        }}
                    >
                        <Label
                            className={`relative flex cursor-pointer gap-2 rounded-lg p-3.5 text-sm font-medium ${
                                domains?.customDomain ? 'cursor-not-allowed opacity-50' : ''
                            } ${
                                selectedDomainType === 'customDomain'
                                    ? 'bg-blue-25 border-2 border-blue-400'
                                    : 'bg-gray-25 border border-gray-300'
                            }`}
                        >
                            {!!domains?.customDomain && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger className="absolute inset-0" />
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <div>
                                                Your account already has a custom domain connected.
                                            </div>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                            <RadioGroup.Item
                                value={'customDomain'}
                                disabled={!!domains?.customDomain}
                                checked={selectedDomainType === 'customDomain'}
                                className="mt-1"
                            >
                                <RadioGroup.Indicator />
                            </RadioGroup.Item>
                            <span>
                                Use your domain and email address (recommended)
                                <span className="block font-normal">
                                    Emails will send from your custom domain name e.g. @example.org.
                                </span>
                            </span>
                        </Label>

                        {engageSubdomains && (
                            <Label
                                className={`relative flex cursor-pointer gap-2 rounded-lg p-3.5 text-sm font-medium ${
                                    domains?.givebutterDomain ? 'cursor-not-allowed opacity-50' : ''
                                } ${
                                    selectedDomainType === 'givebutterDomain'
                                        ? 'bg-blue-25 border-2 border-blue-400'
                                        : 'bg-gray-25 border border-gray-300'
                                }`}
                            >
                                {!!domains?.givebutterDomain && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger className="absolute inset-0" />
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse>
                                                <div>
                                                    Your account already has a Givebutter domain
                                                    connected.
                                                </div>
                                                <Tooltip.Arrow inverse />
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                )}
                                <RadioGroup.Item
                                    value={'givebutterDomain'}
                                    disabled={!!domains?.givebutterDomain}
                                    checked={selectedDomainType === 'givebutterDomain'}
                                    className="mt-1"
                                >
                                    <RadioGroup.Indicator />
                                </RadioGroup.Item>
                                <span>
                                    Use a free Givebutter email address
                                    <span className="block font-normal">
                                        Emails will send from a customized @givebutter-mail.com
                                        address.
                                    </span>
                                </span>
                            </Label>
                        )}
                    </RadioGroup.Root>

                    {selectedDomainType === 'customDomain' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex gap-4 text-sm">
                                <FontAwesomeIcon icon={faCircle1} className="text-gray-300" />
                                <form
                                    onSubmit={handleSubmit(values => submit(values))}
                                    className="flex w-full flex-col gap-4"
                                >
                                    <div>
                                        <div className="flex w-full gap-4">
                                            <Form.Container>
                                                <Form.Label
                                                    name={path('domain')}
                                                    className="flex gap-4"
                                                >
                                                    Add your domain
                                                </Form.Label>
                                                <Form.TextInputRoot
                                                    disabled={!!domains?.customDomain}
                                                    name="domain"
                                                    control={control}
                                                >
                                                    <Form.TextInputForm placeholder="example.org" />
                                                </Form.TextInputRoot>
                                                <Form.Error name={path('domain')} />
                                            </Form.Container>
                                        </div>
                                        <span className="text-xs text-gray-600">
                                            This will be where emails are sent from.
                                        </span>
                                    </div>
                                    <div>
                                        <div className="flex w-full gap-4">
                                            <Form.Container>
                                                <Form.Label name={path('reply_to')}>
                                                    Reply-to email address (optional)
                                                </Form.Label>
                                                <Form.TextInputRoot
                                                    disabled={!!domains?.customDomain}
                                                    name="reply_to"
                                                    control={control}
                                                >
                                                    <Form.TextInputForm placeholder="reply@example.org" />
                                                </Form.TextInputRoot>
                                                <Form.Error name={path('reply_to')} />
                                            </Form.Container>
                                        </div>
                                        <span className="text-xs text-gray-600">
                                            This is the email address that will be displayed if a
                                            contact wants to reply.
                                        </span>
                                    </div>
                                    <div className="flex w-full justify-end">
                                        <Form.SubmitButton disabled={!!domains?.customDomain}>
                                            Add domain
                                        </Form.SubmitButton>
                                    </div>
                                </form>
                            </div>

                            {!!domains?.customDomain && (
                                <div className="flex gap-4">
                                    <FontAwesomeIcon icon={faCircle2} className="text-gray-300" />

                                    <div className="flex flex-col gap-4 text-gray-600">
                                        <Label>Attach DNS records</Label>

                                        <div>
                                            Copy the DNS records provided below into your domain
                                            provider to configure your settings.
                                        </div>

                                        <DNSRecordTable
                                            domain={domains.customDomain}
                                            inPortal={true}
                                        />
                                        <DNSVerification domain={domains.customDomain} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {selectedDomainType === 'givebutterDomain' && (
                        <div>
                            <form
                                onSubmit={handleSubmit(values => submit(values))}
                                className="flex flex-col gap-4"
                            >
                                <div className="text-sm font-medium text-gray-800">
                                    Create a Givebutter subdomain
                                </div>
                                <div className="flex flex-col gap-4 text-sm font-normal text-gray-600">
                                    A subdomain is the address from which your emails will be sent
                                    and distinct from an email sender, which is the specific user
                                    associated with a domain.
                                    <span className="inline-block italic">
                                        Example:{' '}
                                        <span className="font-medium">
                                            emailsender@subdomain.givebutter-mail.com
                                        </span>
                                    </span>
                                </div>
                                <div className="flex w-full items-center">
                                    <Form.Container>
                                        <div className="flex w-full">
                                            <Form.TextInputRoot
                                                disabled={!!domains?.givebutterDomain}
                                                control={control}
                                                name="domain"
                                                inputRootProps={{
                                                    className: 'input-left',
                                                }}
                                            >
                                                <Form.TextInputForm placeholder="subdomain" />
                                            </Form.TextInputRoot>
                                            <div className="flex h-10 items-center justify-center whitespace-nowrap rounded-br rounded-tr border border-l-0 border-gray-300 bg-gray-50 px-3.5 py-2.5">
                                                .givebutter-mail.com
                                            </div>
                                        </div>
                                        <Form.Error name={path('domain')} />
                                    </Form.Container>
                                </div>
                                <div>
                                    <div className="flex w-full gap-4">
                                        <Form.Container>
                                            <Form.Label name={path('reply_to')}>
                                                Reply-to email address (optional)
                                            </Form.Label>
                                            <Form.TextInputRoot
                                                disabled={!!domains?.givebutterDomain}
                                                name="reply_to"
                                                control={control}
                                            >
                                                <Form.TextInputForm placeholder="reply@example.org" />
                                            </Form.TextInputRoot>
                                            <Form.Error name={path('reply_to')} />
                                        </Form.Container>
                                    </div>
                                    <span className="text-xs text-gray-600">
                                        This is the email address that will be displayed if a
                                        contact wants to reply.
                                    </span>
                                </div>
                                <div className="flex w-full justify-end">
                                    <Form.SubmitButton disabled={!!domains?.givebutterDomain}>
                                        Add domain
                                    </Form.SubmitButton>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </Drawer.Body>
            <Drawer.Footer>
                <Drawer.Cancel asChild={true}>
                    <Button variant="transparent">Cancel</Button>
                </Drawer.Cancel>
            </Drawer.Footer>
        </Form.Provider>
    );
};

export { AddDomainDrawer };
